import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, X, Search, Star, Lock, Eye, EyeOff, Upload, FileText, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import SafeImage from "../../components/SafeImage";

const eur = (n, c = "EUR") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0);

function MagazineForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({
    title_fr: initial?.title?.fr || "",
    title_en: initial?.title?.en || "",
    edition: initial?.edition || "",
    cover_url: initial?.cover_url || "",
    pdf_url: initial?.pdf_url || "",
    description_fr: initial?.description?.fr || "",
    description_en: initial?.description?.en || "",
    year: initial?.year || 2026,
    published: initial?.published !== false,
    featured: !!initial?.featured,
    is_premium: !!initial?.is_premium,
    price: initial?.price ?? 19,
    currency: initial?.currency || "EUR",
  });
  const [busy, setBusy] = useState(false);
  const [upStatus, setUpStatus] = useState({ cover: false, pdf: false });

  const upload = async (file, purpose, field) => {
    setUpStatus((s) => ({ ...s, [purpose]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", purpose);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, [field]: url }));
    } finally { setUpStatus((s) => ({ ...s, [purpose]: false })); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: { fr: form.title_fr, en: form.title_en || form.title_fr },
        edition: form.edition,
        cover_url: form.cover_url || null,
        pdf_url: form.pdf_url || null,
        description: { fr: form.description_fr, en: form.description_en || form.description_fr },
        year: parseInt(form.year, 10) || 2026,
        published: !!form.published,
        featured: !!form.featured,
        is_premium: !!form.is_premium,
        price: parseFloat(form.price) || 19,
        currency: form.currency,
      };
      if (initial?.id) await api.put(`/magazines/${initial.id}`, payload);
      else await api.post("/magazines", payload);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.72)" }}>
      <form onSubmit={submit} className="w-full max-w-3xl gai-card gai-sharp p-7 relative max-h-[92vh] overflow-y-auto" data-testid="magazine-form">
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[var(--gai-grey-600)] hover:text-[var(--gai-red)]" aria-label="Fermer">
          <X size={18} />
        </button>
        <div className="overline mb-1">{initial?.id ? "Modifier" : "Nouvelle"}</div>
        <h3 className="serif text-2xl text-[var(--gai-black)] mb-5">Édition magazine</h3>

        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Titre FR *</div>
              <input required className="gai-input" placeholder="Ex : Édition inaugurale"
                value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })}
                data-testid="mag-title-fr" />
            </div>
            <div>
              <div className="overline mb-1">Title EN</div>
              <input className="gai-input"
                value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Édition *</div>
              <input required className="gai-input" placeholder="Ex : N°00 — Février 2026"
                value={form.edition} onChange={(e) => setForm({ ...form, edition: e.target.value })}
                data-testid="mag-edition" />
            </div>
            <div>
              <div className="overline mb-1">Année</div>
              <input type="number" className="gai-input"
                value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Description FR</div>
              <textarea rows={3} className="gai-input resize-none"
                value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} />
            </div>
            <div>
              <div className="overline mb-1">Description EN</div>
              <textarea rows={3} className="gai-input resize-none"
                value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
            </div>
          </div>

          <div className="p-4 border" style={{ borderColor: "var(--gai-grey-200)" }}>
            <div className="overline mb-2 flex items-center gap-2"><BookOpen size={12} /> Couverture</div>
            <div className="flex items-center gap-4 flex-wrap">
              {form.cover_url && (
                <SafeImage src={form.cover_url} alt="cover" className="object-cover" style={{ width: 80, height: 112 }} />
              )}
              <input className="gai-input flex-1 min-w-[220px]" placeholder="URL de la couverture (JPG/PNG)"
                value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
              <label className="gai-btn-outline text-xs cursor-pointer">
                <Upload size={12} /> {upStatus.cover ? "Envoi…" : "Uploader"}
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "cover", "cover_url")} />
              </label>
            </div>
            <p className="text-[0.7rem] text-[var(--gai-grey-500)] mt-2">Format recommandé : <b>800×1120 px</b> (ratio A4).</p>
          </div>

          <div className="p-4 border" style={{ borderColor: "var(--gai-grey-200)" }}>
            <div className="overline mb-2 flex items-center gap-2"><FileText size={12} /> PDF feuilletable</div>
            <div className="flex items-center gap-4 flex-wrap">
              {form.pdf_url && (
                <div className="text-[0.7rem] text-[var(--gai-red)]">✓ PDF lié</div>
              )}
              <input className="gai-input flex-1 min-w-[220px]" placeholder="URL du PDF"
                value={form.pdf_url} onChange={(e) => setForm({ ...form, pdf_url: e.target.value })}
                data-testid="mag-pdf-url" />
              <label className="gai-btn-outline text-xs cursor-pointer" data-testid="mag-upload-pdf-btn">
                <Upload size={12} /> {upStatus.magazine ? "Envoi…" : "Uploader PDF"}
                <input type="file" accept="application/pdf" className="hidden"
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "magazine", "pdf_url")} />
              </label>
            </div>
            <p className="text-[0.7rem] text-[var(--gai-grey-500)] mt-2">Taille max 60 Mo. Le PDF sera utilisé par le flipbook.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 p-4 border" style={{ borderColor: "var(--gai-grey-200)" }}>
            <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)]">
              <input type="checkbox" checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="mag-published" />
              <Eye size={13} /> Publié
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)]">
              <input type="checkbox" checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                data-testid="mag-featured" />
              <Star size={13} /> À la une
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)]">
              <input type="checkbox" checked={form.is_premium}
                onChange={(e) => setForm({ ...form, is_premium: e.target.checked })}
                data-testid="mag-premium" />
              <Lock size={13} /> Premium (payant)
            </label>
          </div>

          {form.is_premium && (
            <div className="grid md:grid-cols-2 gap-3 p-4 border" style={{ borderColor: "var(--gai-red)", background: "rgba(227,30,36,0.04)" }}>
              <div>
                <div className="overline mb-1">Prix</div>
                <input type="number" min="0" step="0.5" className="gai-input"
                  value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <div className="overline mb-1">Devise</div>
                <select className="gai-input" value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  {["EUR", "USD", "XOF", "XAF", "CDF", "MAD", "GBP"].map((c) =>
                    <option key={c} value={c} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{c}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-5 mt-5 border-t" style={{ borderColor: "var(--gai-grey-200)" }}>
          <button type="button" onClick={onCancel} className="gai-btn-ghost text-xs">Annuler</button>
          <button disabled={busy} className="gai-btn-gold text-xs" data-testid="mag-submit">
            {busy ? "Enregistrement…" : (initial?.id ? "Enregistrer" : "Publier l'édition")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminMagazines() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const refresh = async () => {
    setLoading(true);
    const r = await api.get("/admin/magazines").catch(() => ({ data: [] }));
    setItems(r.data || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    if (!q) return items;
    return items.filter((m) => {
      const t = (m.title?.fr || "") + " " + (m.edition || "");
      return t.toLowerCase().includes(q.toLowerCase());
    });
  }, [items, q]);

  const remove = async (m) => {
    if (!window.confirm(`Supprimer définitivement « ${m.title?.fr || m.edition} » ?`)) return;
    await api.delete(`/magazines/${m.id}`);
    refresh();
  };

  const togglePublish = async (m) => {
    await api.put(`/magazines/${m.id}`, {
      title: m.title, edition: m.edition,
      cover_url: m.cover_url, pdf_url: m.pdf_url,
      description: m.description || {},
      published: !m.published, featured: !!m.featured,
      year: m.year || 2026,
      is_premium: !!m.is_premium, price: m.price ?? 19, currency: m.currency || "EUR",
    });
    refresh();
  };

  return (
    <div data-testid="page-admin-magazines">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="overline mb-2">Kiosque digital</div>
          <h1 className="serif text-4xl md:text-5xl text-[var(--gai-black)] leading-none">Magazines &amp; Éditions</h1>
          <p className="mt-2 text-sm text-[var(--gai-grey-500)] max-w-2xl">
            Publiez chaque numéro : couverture, description, PDF feuilletable. Vos PDF existants restent stockés en sécurité, aucun ré-upload nécessaire.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="gai-btn-gold text-xs" data-testid="btn-new-magazine">
          <Plus size={12} /> Nouvelle édition
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gai-grey-500)]" />
          <input className="gai-input pl-9" style={{ paddingLeft: "2.25rem" }} placeholder="Rechercher un numéro…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="magazines-search" />
        </div>
      </div>

      {loading && <div className="text-sm text-[var(--gai-grey-500)]">Chargement…</div>}

      {!loading && filtered.length === 0 && (
        <div className="gai-card gai-sharp p-10 text-center text-[var(--gai-grey-500)]">
          Aucun magazine. Ajoutez la première édition.
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((m) => (
          <div key={m.id} className="gai-card gai-sharp overflow-hidden flex" data-testid={`magazine-card-${m.id}`}>
            <SafeImage src={m.cover_url} alt="" className="object-cover shrink-0" style={{ width: 100, height: 140 }} />
            <div className="p-4 flex-1 flex flex-col min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="overline text-[var(--gai-red)]">{m.edition}</span>
                {!m.published && <span className="text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-grey-500)]">Brouillon</span>}
                {m.featured && <span className="text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-black)]"><Star size={10} className="inline" /> Une</span>}
              </div>
              <div className="serif text-base text-[var(--gai-black)] leading-tight line-clamp-2">{m.title?.fr || "Sans titre"}</div>
              <div className="mt-1 flex items-center gap-2 text-[0.7rem] text-[var(--gai-grey-500)]">
                {m.pdf_url ? <span className="text-[var(--gai-red)]">✓ PDF</span> : <span>Sans PDF</span>}
                {m.is_premium && <span>· <Lock size={10} className="inline" /> {eur(m.price, m.currency)}</span>}
              </div>

              <div className="mt-auto pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--gai-grey-200)" }}>
                <div className="flex gap-1">
                  {m.pdf_url && (
                    <Link to={`/magazine/${m.id}`} target="_blank" className="gai-btn-ghost text-[0.7rem]" data-testid={`magazine-view-${m.id}`}>
                      <BookOpen size={12} /> Lire
                    </Link>
                  )}
                  <button onClick={() => togglePublish(m)} className="gai-btn-ghost text-[0.7rem]"
                    aria-label={m.published ? "Dépublier" : "Publier"}
                    title={m.published ? "Dépublier" : "Publier"}
                    data-testid={`magazine-toggle-${m.id}`}>
                    {m.published ? <><EyeOff size={12} /> Dépublier</> : <><Eye size={12} /> Publier</>}
                  </button>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(m)} className="gai-btn-ghost p-2" aria-label="Modifier" data-testid={`magazine-edit-${m.id}`}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(m)} className="gai-btn-ghost p-2 text-[var(--gai-red)] hover:opacity-80" aria-label="Supprimer" data-testid={`magazine-delete-${m.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <MagazineForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}
